/**
 * CardCarousel — a generic horizontal scroll carousel custom element.
 *
 * Uses native scroll with CSS scroll-snap. Advances one card at a time
 * via prev/next buttons. Arrow states update on scroll and resize.
 *
 * Supports multiple [data-scroller] elements (e.g. inside tabbed panels):
 * the component binds to the first visible (non-hidden) scroller and
 * re-binds on refresh().
 *
 * Public API:
 *   carousel.refresh() — re-find the active scroller and recalculate arrow states
 *   carousel.reset()   — scroll back to the first card
 *
 * DOM contract (data-* attributes):
 *   data-scroller    — the scrollable container(s)
 *   data-prev        — the "previous" button
 *   data-next        — the "next" button
 *   data-live-region — sr-only announcements
 *   data-item-count  — on the host or on [data-tab-panel]: at 3 items, desktop arrow
 *                      controls and the progress bar are hidden (the 3-item layout
 *                      is a grid at md, so there is nothing to scroll)
 *   data-carousel-controls     — wrapper around prev/next buttons
 *   data-carousel-controls-row — mobile-only controls row (always shown on mobile)
 */

class CardCarousel extends HTMLElement {
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number} */
  #scrollRAF = 0;

  /** @type {number} Current leftmost-visible item index */
  #currentIndex = 0;

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  connectedCallback() {
    this.#setup();
  }

  disconnectedCallback() {
    this.#teardown();
  }

  // ─── DOM refs (cached on first access, cleared on disconnect) ──────────────

  /** @type {HTMLElement | null} */
  #scrollerRef = null;

  /** Returns the first visible [data-scroller] (skips elements inside [hidden] ancestors). */
  get #scroller() {
    if (!this.#scrollerRef) {
      for (const el of this.querySelectorAll("[data-scroller]")) {
        if (!el.closest("[hidden]")) {
          this.#scrollerRef = /** @type {HTMLElement} */ (el);
          break;
        }
      }
    }
    return this.#scrollerRef;
  }

  get #prevBtns() {
    return /** @type {NodeListOf<HTMLButtonElement>} */ (this.querySelectorAll("[data-prev]"));
  }

  get #nextBtns() {
    return /** @type {NodeListOf<HTMLButtonElement>} */ (this.querySelectorAll("[data-next]"));
  }

  get #items() {
    const s = this.#scroller;
    return s ? /** @type {HTMLElement[]} */ (Array.from(s.children)) : [];
  }

  get #reducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  // ─── Setup / teardown ──────────────────────────────────────────────────────

  #setup() {
    this.#prevBtns.forEach((btn) => btn.addEventListener("click", this.#handlePrev));
    this.#nextBtns.forEach((btn) => btn.addEventListener("click", this.#handleNext));

    this.#bindScroller();
  }

  /** Bind scroll/resize listeners to the current scroller. */
  #bindScroller() {
    const scroller = this.#scroller;

    if (scroller) {
      scroller.addEventListener("scroll", this.#handleScroll, { passive: true });

      if (!this.#resizeObserver) {
        this.#resizeObserver = new ResizeObserver(this.#handleResize);
      }
      this.#resizeObserver.observe(scroller);
    }

    this.#currentIndex = 0;
    this.#syncCurrentIndex();
    this.#updateControlsVisibility();
    this.#updateArrows();
    this.#updateProgress();
  }

  /** Unbind scroll/resize listeners from the current scroller. */
  #unbindScroller() {
    const scroller = this.#scrollerRef;

    if (scroller) {
      scroller.removeEventListener("scroll", this.#handleScroll);
      this.#resizeObserver?.unobserve(scroller);
    }

    cancelAnimationFrame(this.#scrollRAF);
    this.#scrollerRef = null;
  }

  #teardown() {
    this.#unbindScroller();

    this.#prevBtns.forEach((btn) => btn.removeEventListener("click", this.#handlePrev));
    this.#nextBtns.forEach((btn) => btn.removeEventListener("click", this.#handleNext));

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  // ─── Public API ────────────────────────────────────────────────────────────

  /** Re-find the active scroller (e.g. after a tab switch) and recalculate state. */
  refresh() {
    this.#unbindScroller();
    this.#bindScroller();
  }

  reset() {
    this.#scrollToIndex(0);
  }

  // ─── Scroll navigation (one card at a time) ───────────────────────────────

  /**
   * @param {number} index
   */
  #scrollToIndex(index) {
    const items = this.#items;
    if (!items.length) return;

    const clamped = Math.max(0, Math.min(index, items.length - 1));
    this.#currentIndex = clamped;

    const target = items[clamped];
    if (!target) return;
    target.scrollIntoView({
      behavior: this.#reducedMotion ? "instant" : "smooth",
      block: "nearest",
      inline: "start"
    });
  }

  /**
   * Determine which item is currently at the scroll origin.
   * Uses a small tolerance so sub-pixel rounding doesn't cause misses.
   */
  #syncCurrentIndex() {
    const scroller = this.#scroller;
    if (!scroller) return;

    const scrollLeft = scroller.scrollLeft;
    const items = this.#items;
    let closest = 0;
    let closestDist = Infinity;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item) continue;
      const dist = Math.abs(item.offsetLeft - scrollLeft);
      if (dist < closestDist) {
        closestDist = dist;
        closest = i;
      }
    }

    this.#currentIndex = closest;
  }

  // ─── Progress bar (paged: fixed-width fill that slides, never grows) ───────

  /**
   * The fill is one "page" wide, where pages = number of positions the next
   * button can land on (each reachable item start + the clamped end position).
   * With one click needed to reach the end, the fill is 50% wide and slides
   * from the left half to the right half. Width + transform are both driven
   * here; progress is continuous so trackpad/swipe scrolling moves it smoothly.
   */
  #updateProgress() {
    const scroller = this.#scroller;
    const fill = /** @type {HTMLElement | null} */ (this.querySelector("[data-progress-fill]"));
    if (!scroller || !fill) return;

    const maxScroll = scroller.scrollWidth - scroller.clientWidth;

    if (maxScroll <= 1) {
      fill.style.width = "100%";
      fill.style.transform = "translateX(0%)";
      return;
    }

    // Items whose start is reachable before the scroller clamps at the end.
    const reachable = this.#items.filter((item) => item.offsetLeft < maxScroll - 1).length;
    const pages = reachable + 1;

    const progress = Math.min(1, Math.max(0, scroller.scrollLeft / maxScroll));

    // translateX % is relative to the fill's own width, so (pages - 1) * 100%
    // lands the fill flush right when progress is 1.
    fill.style.width = `${100 / pages}%`;
    fill.style.transform = `translateX(${progress * (pages - 1) * 100}%)`;
  }

  // ─── Controls visibility (3-item panels use a grid on desktop, not a slider) ─

  /** @returns {boolean} */
  #shouldHideControlsOnDesktop() {
    if (this.dataset.itemCount === "3") return true;

    const activePanel = /** @type {HTMLElement | null} */ (this.querySelector("[data-tab-panel]:not([hidden])"));
    return activePanel?.dataset.itemCount === "3";
  }

  #updateControlsVisibility() {
    const hideOnDesktop = this.#shouldHideControlsOnDesktop();

    this.querySelectorAll("[data-carousel-controls]").forEach((el) => {
      if (el.closest("[data-carousel-controls-row]")) return;

      /** @type {HTMLElement} */ (el).classList.toggle("md:hidden", hideOnDesktop);
    });

    const progressBar = /** @type {HTMLElement | null} */ (this.querySelector("[data-progress-bar]"));
    progressBar?.classList.toggle("md:!hidden", hideOnDesktop);
  }

  // ─── Arrow state ───────────────────────────────────────────────────────────

  #updateArrows() {
    const scroller = this.#scroller;

    if (!scroller) {
      this.#prevBtns.forEach((btn) => this.#setDisabled(btn, true));
      this.#nextBtns.forEach((btn) => this.#setDisabled(btn, true));
      return;
    }

    const atStart = scroller.scrollLeft <= 1;
    const atEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1;

    this.#prevBtns.forEach((btn) => this.#setDisabled(btn, atStart));
    this.#nextBtns.forEach((btn) => this.#setDisabled(btn, atEnd));
  }

  /**
   * @param {HTMLButtonElement | null} btn
   * @param {boolean} disabled
   */
  #setDisabled(btn, disabled) {
    if (!btn) return;
    btn.setAttribute("aria-disabled", String(disabled));
    btn.classList.toggle("opacity-40", disabled);
    btn.classList.toggle("pointer-events-none", disabled);
  }

  // ─── Handlers ──────────────────────────────────────────────────────────────

  #handlePrev = () => {
    this.#syncCurrentIndex();
    this.#scrollToIndex(this.#currentIndex - 1);
  };

  #handleNext = () => {
    this.#syncCurrentIndex();
    this.#scrollToIndex(this.#currentIndex + 1);
  };

  #handleScroll = () => {
    cancelAnimationFrame(this.#scrollRAF);
    this.#scrollRAF = requestAnimationFrame(() => {
      this.#syncCurrentIndex();
      this.#updateArrows();
      this.#updateProgress();
    });
  };

  #handleResize = () => {
    this.#syncCurrentIndex();
    this.#updateControlsVisibility();
    this.#updateArrows();
    this.#updateProgress();
  };
}

if (!customElements.get("card-carousel")) {
  customElements.define("card-carousel", CardCarousel);
}
